import { useCallback, useContext, type VoidFunctionComponent } from 'react';
import ContentLoader from 'react-content-loader';
import { JSONTree } from 'react-json-tree';
import XMLViewer from 'react-xml-viewer';
import type { JsonObject } from 'type-fest';

import {
  AttributionTextReportPublicationStatuses,
  JSONReportPublicationStatuses,
  ReportPublicationStatusToString,
  TextReportPublicationStatuses,
  XMLReportPublicationStatuses,
} from '../../../../shared/model-typings/projectgrouprelease';
import RouterLink from '../../../components/generic/Link/RouterLink';
import Loader from '../../../components/generic/Loader';
import PaginationFooter from '../../../components/generic/Pagination/PaginationFooter';
import Text from '../../../components/generic/Text';
import ReportViewer from '../../../components/ReportViewer';
import { RouterContext, useParams } from '../../../contexts/RouterContext';
import useAsyncRequest from '../../../hooks/useAsyncRequest';
import { get } from '../../../redux/api';
import COLORS from '../../../theme/colors';
import { JSONTreeTheme } from '../../../theme/jsontree';
import { containEvent } from '../../../utils';
import { releaseGroupDependenciesRoute, releaseGroupReleaseRoute, releaseReportRoute } from '../../helpers/routes';
import NotFound from '../../NotFound';
import usePagination from '../hooks/usePagination';
import useViewType, { fossaAnalysisViewType } from '../hooks/useViewType';
import type { ReleaseGroupRelease } from '../../../../shared/publicPortal/types';

import FossaAnalysisReport from './FossaAnalysisReport';
import type { ReleaseGroupParams } from './types';

import styles from './ReleaseGroup.module.less';

type ReportParams = {
  isLoading: boolean;
  release?: ReleaseGroupRelease;
};

const VersionTitle: VoidFunctionComponent<ReportParams> = ({ isLoading, release }) => {
  if (isLoading) {
    return (
      <ContentLoader
        speed={2}
        width={150}
        height={22}
        foregroundColor={COLORS.fossaGray05}
        backgroundColor={COLORS.fossaGray10}
      >
        <rect x="0" y="0" rx="3" ry="3" width="150" height="22" />
      </ContentLoader>
    );
  }

  if (!release) {
    return <Text.H4>Version unknown</Text.H4>;
  }

  return <Text.H4>Version {release.title}</Text.H4>;
};

const Report: VoidFunctionComponent<ReportParams> = ({ isLoading, release }) => {
  const { slug, releaseGroupId, releaseId } = useParams<ReleaseGroupParams>();
  const { page, count, setPage, setCount, totalItems, setTotalItems } = usePagination();
  const { replacePath } = useContext(RouterContext);
  const viewType = useViewType();
  const defaultViewRoute = releaseGroupReleaseRoute(slug, Number(releaseGroupId), Number(releaseId));
  const dependenciesRoute = releaseGroupDependenciesRoute(slug, Number(releaseGroupId), Number(releaseId));
  /**
   * When switching between releases, the report data for the previously visible
   * release is still stored in the value attribute of `useAsyncRequest` (and
   * `isLoading` is still false) until its effect starts the new request.
   * This can cause React to throw an error if the previous report was JSON and
   * the currently selected release would render plain text because React will
   * now be trying to render the JSON object as a React Element.
   *
   * To avoid this, the fetched report is tagged with the release it belongs to,
   * and a report for any other release is treated as still loading.
   */
  const getReleaseReport = useCallback(async () => {
    if (!release) {
      return { data: undefined };
    }

    let responseType: 'json' | 'text' = 'text';
    if ((JSONReportPublicationStatuses as readonly unknown[]).includes(release.publishedOnPortal)) {
      responseType = 'json';
    }

    const { data } = await get(releaseReportRoute(slug, release.id), true, undefined, { responseType });
    return { data: data === undefined ? undefined : { releaseId: release.id, report: data } };
  }, [release, slug]);
  const {
    value: releaseReport,
    isLoading: isLoadingReport,
    errors: reportErrors,
  } = useAsyncRequest(getReleaseReport);
  const reportData = releaseReport?.report;
  const isReportForAnotherRelease = !reportErrors && releaseReport?.releaseId !== release?.id;

  if (!isLoading && !release) {
    return (
      <div className={styles.report}>
        <NotFound />
      </div>
    );
  }

  const getReportElement = () => {
    if (isLoading || isLoadingReport || isReportForAnotherRelease) {
      return <Loader />;
    }

    if ((JSONReportPublicationStatuses as readonly unknown[]).includes(release?.publishedOnPortal)) {
      return <JSONReport report={reportData as JsonObject} />;
    }
    if ((XMLReportPublicationStatuses as readonly unknown[]).includes(release?.publishedOnPortal)) {
      return <XMLReport report={reportData as string} />;
    }
    if ((TextReportPublicationStatuses as readonly unknown[]).includes(release?.publishedOnPortal)) {
      return <PlaintextReport report={reportData as string} />;
    }
    if ((AttributionTextReportPublicationStatuses as readonly unknown[]).includes(release?.publishedOnPortal)) {
      return <PlaintextReport report={reportData as string} />;
    }

    return undefined;
  };

  return (
    <div className={styles.reportContainer}>
      <div className={styles.report}>
        <div className={styles.reportHeader}>
          <VersionTitle isLoading={isLoading} release={release} />
        </div>
        <ReportViewer>
          <ReportViewer.Format>
            <span>View:</span>
            {release?.publishedOnPortal && (
              <RouterLink
                activeClassName={styles.selectedViewLink}
                className={styles.viewLink}
                to={defaultViewRoute}
                onClick={containEvent(() => {
                  replacePath(defaultViewRoute);
                })}
              >
                {ReportPublicationStatusToString[release.publishedOnPortal]}
              </RouterLink>
            )}
            <RouterLink
              activeClassName={styles.selectedViewLink}
              className={styles.viewLink}
              to={dependenciesRoute}
              onClick={containEvent(() => {
                replacePath(dependenciesRoute);
              })}
            >
              Dependencies
            </RouterLink>
          </ReportViewer.Format>
          <ReportViewer.Content>
            {viewType === fossaAnalysisViewType && release ? (
              <div className={styles.fossaAnalysisReportContent}>
                <FossaAnalysisReport release={release} setTotalItems={setTotalItems} page={page} count={count} />
              </div>
            ) : (
              <div className={styles.reportContent}>{getReportElement()}</div>
            )}
          </ReportViewer.Content>
        </ReportViewer>
      </div>
      {viewType === fossaAnalysisViewType && !!totalItems && (
        <PaginationFooter page={page} count={count} totalItems={totalItems} setPage={setPage} setCount={setCount} />
      )}
    </div>
  );
};

const JSONReport: VoidFunctionComponent<{ report: JsonObject }> = ({ report }) => (
  <JSONTree
    data={report}
    theme={JSONTreeTheme}
    shouldExpandNodeInitially={(_keyPath, _data, level) => level < 5}
    invertTheme
    hideRoot
  />
);

const XMLReport: VoidFunctionComponent<{ report: string }> = ({ report }) => (
  <XMLViewer xml={report} collapsible initalCollapsedDepth={4} />
);

const PlaintextReport: VoidFunctionComponent<{ report: string }> = ({ report }) => <pre>{report}</pre>;

export default Report;
